import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { initialUserProvider, initialUserToken } from '../../providers/initial-user';
import {
  AppError,
  AppErrorService,
  AuthService,
  PageErrorHandlerComponent,
  PageSpinnerComponent,
  TopbarComponent,
  TopbarDropdownComponent,
  TopbarDropdownItemComponent,
} from 'shared';
import { catchError, filter, map, takeUntil } from 'rxjs/operators';
import { CurrentUserService } from '../../services/current-user.service';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { IUser } from '../../interfaces/user';
import { Observable, of, Subject } from 'rxjs';
import { NgClass, AsyncPipe } from '@angular/common';
import { DeveloperMenuComponent } from '../../components/developer-menu/developer-menu.component';
import { TranslateModule } from '@ngx-translate/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';

@Component({
    selector: 'app-root-page',
    templateUrl: './root-page.component.html',
    styleUrls: ['./root-page.component.scss'],
    providers: [initialUserProvider],
    imports: [
    DeveloperMenuComponent,
    TopbarComponent,
    NgClass,
    TopbarDropdownComponent,
    TopbarDropdownItemComponent,
    RouterLink,
    PageSpinnerComponent,
    PageErrorHandlerComponent,
    RouterOutlet,
    AsyncPipe,
    TranslateModule
],
})
export class RootPageComponent implements OnInit, OnDestroy {
  currentUser$: Observable<IUser>;
  pageError$: Observable<AppError>;
  showDeveloperUI$ = this.currentUserService.currentUser$.pipe(
    map((user) => user.accessClaimsGroupNames.includes('Admin')),
    catchError(() => of(false)),
  );
  showArchiveButton$: Observable<boolean>;
  showSupportButton$: Observable<boolean>;
  showCommissionButton$: Observable<boolean>;

  /** Currently active MSAL account, kept up to date via MsalBroadcastService. */
  activeAccount: AccountInfo | null = null;
  /** True once MSAL has finished any in-flight interaction. */
  msalReady = false;

  private readonly destroying$ = new Subject<void>();

  constructor(
    private readonly currentUserService: CurrentUserService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly errorService: AppErrorService,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    @Inject(initialUserToken) initialUser$: Observable<IUser>,
  ) {
    this.pageError$ = this.errorService.createPageErrorObservable([initialUser$]);
    this.currentUser$ = this.currentUserService.currentUser$;

    this.showCommissionButton$ = this.currentUserService.hasClaim('Commission');
    this.showSupportButton$ = this.currentUserService.hasClaim('Support');
    this.showArchiveButton$ = this.currentUserService.hasClaim('Support');
  }

  ngOnInit(): void {
    // Track when MSAL is idle so the UI can react to "logged in / logged out" state.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.msalReady = true;
        this.setActiveAccountFromMsal();
      });

    // React to discrete MSAL auth events (login, logout, token refresh).
    //
    // NB: do NOT include ACTIVE_ACCOUNT_CHANGED here — it is fired by
    // `setActiveAccount()`, which `setActiveAccountFromMsal()` calls; adding
    // it would create an infinite loop and blow the call stack.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this.destroying$),
      )
      .subscribe(() => this.setActiveAccountFromMsal());
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  navigateToRoot() {
    this.router.navigate(['']);
  }

  logout() {
    // AuthService owns the MSAL logout redirect.
    this.authService.signout();
  }

  private setActiveAccountFromMsal(): void {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null;
    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }
    this.activeAccount = account;
  }
}
